import { useMemo, useState } from "react";

import { ShareDialog, type ShareSubject } from "#app/components/sharing/share-dialog";
import { viewVisual } from "#app/components/view-options";
import type { EntityViewRow } from "#app/lib/data";
import { useWorkspace } from "#app/lib/data/app-provider";
import { useEntityViewSharing } from "#app/lib/hooks/use-entity-view-sharing";
import { viewHref } from "#app/lib/utils/entity-hrefs";

// Workspace-dashboard wrapper around the generic ShareDialog. Owns the
// debounced search → useEntityViewSharing wiring, the dashboard's own
// glyph and name as the title, and its deep link.

type DashboardShareDialogProps = {
  /** The dashboard being shared — null keeps the dialog closed. */
  view: EntityViewRow | null;
  onOpenChange: (open: boolean) => void;
};

export function DashboardShareDialog({
  view,
  onOpenChange,
}: DashboardShareDialogProps) {
  const { activeWorkspace } = useWorkspace();
  const [activeSearch, setActiveSearch] = useState<string | undefined>(
    undefined,
  );

  const sharing = useEntityViewSharing(view?.id ?? null, activeSearch);
  const subject = useMemo(
    (): ShareSubject | null =>
      view ? { kind: "view", ...viewVisual(view), name: view.name } : null,
    [view],
  );
  // A workspace dashboard is its own page under the Dashboards module.
  const shareUrl =
    view && activeWorkspace
      ? viewHref(
          activeWorkspace.urlPath,
          { kind: "workspace", id: activeWorkspace.workspaceId },
          view,
        )
      : null;

  return (
    <ShareDialog
      open={view != null}
      onOpenChange={onOpenChange}
      subject={subject}
      subjectNoun="dashboard"
      shareUrl={shareUrl}
      sharing={sharing}
      onSearchChange={setActiveSearch}
    />
  );
}
